import { useTranslations } from "next-intl";
import { Fragment } from "react";

import styles from "./retailersList.module.scss";

interface UIProps {
  content: any[] | undefined;
}

// Storyblok fields are free text and occasionally come back empty or as the
// wrong shape. Render nothing rather than letting one bad row take the page.
const asText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const UI = ({ content }: UIProps) => {
  const t = useTranslations();

  // Country names double as optional message keys ("Norway" -> "Norge"), but
  // most of them have no translation, so fall back to the raw value.
  const translateCountry = (value: unknown) => {
    const country = asText(value);
    if (!country) return "";
    return t.has(country as any) ? t(country as any) : country;
  };

  let previousCountry = "";

  return (
    <div className={styles.list}>
      <div className={styles.header}>
        <div className={styles.cell}>{t("store")}</div>
        <div className={styles.cell}>{t("country")}</div>
        <div className={styles.cell}>{t("city")}</div>
        <div className={styles.cell}>{t("address")}</div>
        <div className={styles.cell}>{t("contact")}</div>
      </div>
      {content?.map((retailer) => {
        const { name, country, city, address, zip, phone, mapLink, email } =
          retailer?.content ?? {};

        const countryLabel = translateCountry(country);
        const isNewCountry = countryLabel !== previousCountry;
        previousCountry = countryLabel;

        const postalLine = [asText(zip), asText(city)]
          .filter(Boolean)
          .join(" ");
        const phoneNumber = asText(phone);
        const emailAddress = asText(email?.email) || asText(email?.url);

        return (
          <Fragment key={retailer.id}>
            {isNewCountry && countryLabel && (
              <p className={styles.countryHeading}>{countryLabel}</p>
            )}
            <div className={styles.retailer}>
              <div className={`${styles.cell} ${styles.name}`}>
                {asText(name)}
              </div>
              <div className={`${styles.cell} ${styles.country}`}>
                {countryLabel}
              </div>
              <div className={`${styles.cell} ${styles.city}`}>
                {asText(city)}
              </div>
              <div className={`${styles.cell} ${styles.address}`}>
                {asText(address) && <p>{asText(address)}</p>}
                {postalLine && <p>{postalLine}</p>}
                {mapLink?.url && (
                  <p>
                    <a href={mapLink.url} target="_blank" rel="noreferrer">
                      {t("viewOnMap")}
                    </a>
                  </p>
                )}
              </div>
              <div className={`${styles.cell} ${styles.contact}`}>
                {phoneNumber && (
                  <p>
                    <a href={`tel:${phoneNumber.replace(/\s+/g, "")}`}>
                      {phoneNumber}
                    </a>
                  </p>
                )}
                {emailAddress && (
                  <p>
                    <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
                  </p>
                )}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

export default UI;
